/**
 * The weather now, one reading for every place that shows it: the header's
 * pill, Today's weather, the Plants page's bar and the Weather page.
 *
 * The server decides all of it (B77, W24 Job D): the header answer (GET
 * /header, through WeatherContext) carries `now`, the forecast period covering
 * this moment, already said: the reading in the gardener's unit ("64°F"), the
 * sky in their language ("Mayormente soleado"), the sky's icon key and the
 * period's name. The app shows it as it is: no unit formatting, no words of
 * its own, no icon chosen from the words.
 *
 * Where: WeatherPill, TodayPage, GardenConditionsBar, WeatherPage.
 * API: none of its own (GET /header through WeatherContext).
 *
 * @module useWeatherNow
 */

import { useWeatherContext } from './useWeatherContext';
import type { SkyKey } from '@/shared/api/header';

/** The weather now, as every place shows it. */
export interface WeatherNow {
  /** The reading in the gardener's unit, "64°F", the server's; null when it sent none. */
  display: string | null;
  /** The sky in the gardener's language, "Mostly sunny", the server's. */
  words: string;
  /** The sky's icon key, the server's (SkyIcon draws it). */
  icon: SkyKey;
  /** Whether it is the day's period. */
  isDaytime: boolean;
  /** The period's name, "This Afternoon", in the gardener's language (the server's). */
  label: string | null;
}

/**
 * The weather now: the server's reading, as sent.
 *
 * @returns The reading, or null until the server has sent one (or has none).
 */
export function useWeatherNow(): WeatherNow | null {
  const { now } = useWeatherContext();
  if (!now) return null;
  return {
    display: now.display || null,
    words: now.words ?? '',
    icon: now.icon,
    isDaytime: now.isDaytime,
    label: now.label ?? null,
  };
}
